import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, Button } from 'react-bootstrap'
import styled from 'styled-components'
import InLineVariantDisplay from './InLineVariantDisplay'
import MediaQuery from 'react-responsive'
import { colors } from '../../../styles/colors'
import { ContingentRows, CategoryRows } from '../../ProductsOverview/Structure'
import { CustomizationIcon } from '../../Icons/CustomizationIcon'

const StyledWrapper = styled.div`
  padding: 10px;
  border-bottom: 1px solid ${colors.gray3};
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 480px) {
    padding: 6px 8px;
  }

  .row {
    margin-left: 0;
    margin-right: 0;
  }

  .col {
    padding-left: 0;
    padding-right: 0;
  }

  * {
    max-width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
  }
`

const MobileArticleWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;

  .article-meta {
    margin-bottom: 8px;
  }

  .button-group {
    display: flex;
    flex-direction: row;
    gap: 8px;

    button,
    div {
      flex: 1 1 0;
      min-width: 0;
      max-width: 100%;
    }
  }

  @media screen and (max-width: 365px) {
    .button-group {
      flex-direction: column;
    }
  }
`

const ArticlesTable = memo(function ArticlesTable({
  contingents,
  setOpenConfigurationModal,
  setOpenVariantModal,
}) {
  const { t } = useTranslation()
  const ListItem = ({ article }) => (
    <tr>
      <td colSpan='5' style={{ padding: 0 }}>
        <div style={{ width: '100%', overflowX: 'hidden' }}>
          <StyledWrapper>
            <MobileArticleWrapper>
              <div className='article-meta'>
                <div>{article.article.displayName}</div>
                <small className='text-uppercase'>
                  ({article.article.articleNumber})
                </small>
              </div>
              <div className='button-group'>
                {article.isCustomizable && (
                  <Button
                    size='sm'
                    variant={
                      article.configurationIsMissing ||
                      !article.hasSelectedConfigurations
                        ? 'secondary'
                        : 'success'
                    }
                    onClick={() => setOpenConfigurationModal(article)}
                  >
                    <CustomizationIcon
                      color='white'
                      width='15px'
                      height='15px'
                    />
                  </Button>
                )}
                {article.isWithSizes && (
                  <InLineVariantDisplay
                    lastSelectedVariant={article.lastSelectedVariant}
                    setOpenVariantModal={setOpenVariantModal}
                    article={article}
                  />
                )}
              </div>
            </MobileArticleWrapper>
          </StyledWrapper>
        </div>
      </td>
    </tr>
  )
  const TableItem = ({ article }) => (
    <tr>
      <td>{article.article.displayName}</td>
      <td>{article.article.articleNumber}</td>
      <td>
        {article.isCustomizable ? (
          <Button
            style={{ flex: 1 }}
            variant={
              article.configurationIsMissing ||
              !article.hasSelectedConfigurations
                ? 'secondary'
                : 'success'
            }
            onClick={() => setOpenConfigurationModal(article)}
          >
            <CustomizationIcon color='white' width='15px' height='15px' />
          </Button>
        ) : null}
      </td>
      {article.isWithSizes ? (
        <td>
          <InLineVariantDisplay
            lastSelectedVariant={article.lastSelectedVariant}
            setOpenVariantModal={setOpenVariantModal}
            article={article}
          />
        </td>
      ) : null}
    </tr>
  )
  return (
    <>
      <Table borderless style={{ tableLayout: 'fixed', width: '100%' }}>
        <tbody>
          {contingents.map((contingent) => (
            <ContingentRows key={contingent.id} contingent={contingent}>
              {contingent.categories.map((category) => {
                const configurationsAvailable = category.articles.find(
                  (article) => article.isCustomizable
                )
                const sizesAvailable = category.articles.find(
                  (article) => article.isWithSizes
                )
                return (
                  <CategoryRows
                    key={category.id}
                    category={category}
                    hideMaxedOut
                    hideCounter
                  >
                    <MediaQuery minWidth={993}>
                      <tr>
                        <th>{t('productDetail:articleDescription')}</th>
                        <th>{t('productDetail:articleNumber')}</th>
                        {configurationsAvailable ? (
                          <th style={{ textAlign: 'center' }}>
                            {t('productDetail:customize')}
                          </th>
                        ) : (
                          <th></th>
                        )}
                        {sizesAvailable ? (
                          <th>{t('productDetail:variantTableSize')}</th>
                        ) : null}
                      </tr>
                      {category.articles.map((article) => (
                        <TableItem key={article.article.id} article={article} />
                      ))}
                    </MediaQuery>
                    <MediaQuery maxWidth={992}>
                      {category.articles.map((article) => (
                        <ListItem key={article.article.id} article={article} />
                      ))}
                    </MediaQuery>
                  </CategoryRows>
                )
              })}
            </ContingentRows>
          ))}
        </tbody>
      </Table>
    </>
  )
})

export default ArticlesTable
